/**
 * Fetch wrapper against the PFH Worker API.
 *
 * Same origin — dashboard is served as static assets by the Worker.
 * In dev, Vite proxies /api/* to http://localhost:8787.
 *
 * Auth: Better Auth session cookie (pfh.session) is set on sign-in
 * and sent with every request via credentials: "include".
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body: unknown = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Extract a human-readable message from an ApiError, unwrapping the
 * server's error envelope when possible. For 400s the Worker returns
 * `{ error, code, message, detail, requestId }` — zod validation
 * failures carry the failing field path + reason in `detail[].path` /
 * `detail[].message`. Surface that in toasts so the user doesn't have
 * to guess which field the server rejected.
 */
export function extractApiErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as
      | {
          message?: string;
          detail?: Array<{ path?: (string | number)[]; message?: string }>;
          code?: string;
        }
      | null;
    if (body?.detail && Array.isArray(body.detail) && body.detail.length > 0) {
      const parts = body.detail
        .slice(0, 3)
        .map((issue) => {
          const path = (issue.path ?? []).join(".");
          const msg = issue.message ?? "invalid";
          return path ? `${path}: ${msg}` : msg;
        })
        .join("; ");
      const prefix = body.message ?? `${e.status}`;
      return `${prefix} — ${parts}`;
    }
    if (body?.message) return body.message;
    return e.message;
  }
  if (e instanceof Error) return e.message;
  return String(e);
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    ...init,
    headers: {
      accept: "application/json",
      "content-type": "application/json",
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, `${init.method ?? "GET"} ${path} → ${res.status}`, body);
  }
  if (res.status === 204) return null as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  del: <T>(path: string) => apiFetch<T>(path, { method: "DELETE" }),
};
